import { Box, Link, Stack } from "@mui/material";
import type { GetAvailabilitiesResponse } from "../../shared/web/schema/getAvailabilities";
import ClassNames from "../../util/ClassNames";
import { ColorHexCodes } from "../../util/Color";
import Time from "../../util/Time";
import { StyledText } from "../StyledText";
import style from "./TimeTable.module.css";

type StaticTimeTableArgs = {
  availability: GetAvailabilitiesResponse;
  cellLink: string;
  rowCount: number;
};

export function StaticTimeTable({ availability, cellLink, rowCount }: StaticTimeTableArgs) {
  // スロットオフセット（時間）を取得
  const allSlots = availability.slot_offsets_millis;

  // rowCountで表示するスロット数を制限
  const slots = allSlots.slice(0, rowCount);
  const hasMoreSlots = allSlots.length > rowCount;

  // 列のラベル(日付)を作成する - daily_availabilitiesのstart_of_day_millisを使用
  const daysLabelItem = createDaysLabel(availability.daily_availabilities);

  // 行のラベル(時間)を作成する
  const timeLabels = createStaticTimeLabels(slots, hasMoreSlots);

  // 予約セルを作成する
  const dataCells = createStaticDataCells(slots, availability, cellLink, hasMoreSlots);

  return (
    <Stack className={ClassNames(style.cellWrapper)} sx={{ textAlign: "center" }}>
      <Stack style={{ background: "white", flexDirection: "row" }}>
        <Box className={ClassNames(style.grow, style.cell, style.timeColumn)}>日時</Box>
        {daysLabelItem}
      </Stack>
      <Stack style={{ flexDirection: "row" }}>
        <Stack className={ClassNames(style.grow, style.timeColumn)}>{timeLabels}</Stack>
        {dataCells}
      </Stack>
    </Stack>
  );
}

function createDaysLabel(dailyAvailabilities: GetAvailabilitiesResponse["daily_availabilities"]) {
  return dailyAvailabilities.map((dayData) => {
    const timeString = new Time(dayData.start_of_day_millis).DateStrings();
    let dateColor = ColorHexCodes.CharacterBlack;
    let dayColor = "7a7a7a";
    if (timeString.day === "土") {
      dateColor = "688feb";
      dayColor = "688feb";
    } else if (timeString.day === "日") {
      dateColor = ColorHexCodes.RequiredRed;
      dayColor = ColorHexCodes.RequiredRed;
    }
    return (
      <Stack className={ClassNames(style.grow, style.cell)} key={dayData.start_of_day_millis}>
        <Stack style={{ color: `#${dateColor}` }}>
          <StyledText textStyle="NoMargin">{`${timeString.month}/${timeString.date}`}</StyledText>
        </Stack>
        <Stack style={{ color: `#${dayColor}` }}>
          <StyledText textStyle="TimeTableDay">{`${timeString.day}`}</StyledText>
        </Stack>
      </Stack>
    );
  });
}

function createStaticTimeLabels(slots: number[], hasMoreSlots: boolean) {
  return slots.map((slot, i) => {
    const totalMinutes = slot / Time.AMinute;
    const hour = Math.floor(totalMinutes / 60);
    const min = (totalMinutes % 60).toString().padStart(2, "0");
    const isLastSlot = i === slots.length - 1;
    return (
      <Box className={ClassNames(style.cell, style.timeColumn)} key={slot} style={{ height: "45px" }}>
        {isLastSlot && hasMoreSlots ? `${hour}:${min}以降` : `${hour}:${min}`}
      </Box>
    );
  });
}

function createStaticDataCells(slots: number[], availability: GetAvailabilitiesResponse, cellLink: string, hasMoreSlots: boolean) {
  const allSlots = availability.slot_offsets_millis;

  // 日付ごとのデータがdaily_availabilitiesとして返ってくる
  return availability.daily_availabilities.map((dayData) => {
    const startOfDay = dayData.start_of_day_millis;
    const column = slots.map((slotOffset, slotIndex) => {
      const isLastDisplayedSlot = slotIndex === slots.length - 1;
      const timestamp = startOfDay + slotOffset;

      let slotSymbol = "×";
      let symbolColor = "";
      let classNameString = ClassNames(style.cell, style.unavailable, style.availablityCell);
      let clickable = false;
      let link = `${cellLink}&startAt=${timestamp}`;

      if (isLastDisplayedSlot && hasMoreSlots) {
        // 最後のスロットで、まだ表示されていないスロットがある場合
        // これ以降の空きスロット数をカウント
        const remainingSlotCount = allSlots.length - slotIndex;
        let availableCount = 0;
        for (let i = slotIndex; i < allSlots.length; i++) {
          if (dayData.availability_flags[i]) {
            availableCount++;
          }
        }
        link = cellLink;

        if (availableCount >= remainingSlotCount / 2) {
          slotSymbol = "◎";
          symbolColor = `#${ColorHexCodes.RequiredRed}`;
          classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
          clickable = true;
        } else if (availableCount > 0) {
          slotSymbol = "△";
          symbolColor = `#${ColorHexCodes.MainColorGreen}`;
          classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
          clickable = true;
        }
      } else {
        // 通常のスロット
        const isAvailable = dayData.availability_flags[slotIndex] ?? false;
        if (isAvailable) {
          slotSymbol = "◎";
          symbolColor = `#${ColorHexCodes.RequiredRed}`;
          classNameString = ClassNames(style.cell, style.clickable, style.availablityCell);
          clickable = true;
        }
      }

      if (clickable) {
        return (
          <Link className={classNameString} href={link} key={slotOffset} style={{ color: symbolColor, textDecoration: "none" }}>
            {slotSymbol}
          </Link>
        );
      } else {
        return (
          <Box className={classNameString} key={slotOffset} style={{ color: symbolColor }}>
            {slotSymbol}
          </Box>
        );
      }
    });
    return (
      <Stack className={style.grow} key={dayData.start_of_day_millis}>
        {column}
      </Stack>
    );
  });
}
